/**
 * Hotspot map (pillar B) — the wire shapes shared by `hotspot.list` /
 * `hotspot.get`, `GET /api/projects/:id/hotspots` and the dashboard's
 * URL state.
 */
import { z } from "zod";
import { findingQueueQuerySchema } from "../priority/finding-queue.js";

/** Grouping keys the map can rank by (`route` is the "Page" key). */
export const HOTSPOT_KEYS = [
  "capability",
  "human_test",
  "scenario",
  "persona",
  "route",
] as const;
export type HotspotBy = (typeof HOTSPOT_KEYS)[number];

export const hotspotWindowSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("lastRuns"), n: z.number().int().min(1).max(50) }),
  z.object({ kind: z.literal("days"), n: z.number().int().min(1).max(365) }),
  z.object({ kind: z.literal("all") }),
]);
export type HotspotWindow = z.infer<typeof hotspotWindowSchema>;

export const DEFAULT_HOTSPOT_WINDOW: HotspotWindow = { kind: "lastRuns", n: 5 };

/** Window ⇄ compact URL token: `last5`, `30d`, `all`. */
export function formatHotspotWindow(w: HotspotWindow): string {
  if (w.kind === "all") return "all";
  return w.kind === "lastRuns" ? `last${w.n}` : `${w.n}d`;
}

export function parseHotspotWindow(
  token: string | null | undefined,
): HotspotWindow {
  if (token === "all") return { kind: "all" };
  const last = token?.match(/^last(\d{1,2})$/);
  const days = token?.match(/^(\d{1,3})d$/);
  const candidate = last
    ? { kind: "lastRuns", n: Number(last[1]) }
    : days
      ? { kind: "days", n: Number(days[1]) }
      : null;
  const parsed = hotspotWindowSchema.safeParse(candidate);
  return parsed.success ? parsed.data : DEFAULT_HOTSPOT_WINDOW;
}

/** The queue filters that also narrow the map (shared filter bar). */
export const hotspotFiltersSchema = findingQueueQuerySchema.pick({
  severities: true,
  capabilityId: true,
  capabilityScope: true,
  outlineId: true,
  personaId: true,
  lens: true,
  judgeCited: true,
  search: true,
  period: true,
  periodMode: true,
  tz: true,
  environment: true,
  branch: true,
  committedOnly: true,
});
export type HotspotFilters = z.infer<typeof hotspotFiltersSchema>;

export const hotspotListInputSchema = z.object({
  projectId: z.string().uuid(),
  by: z.enum(HOTSPOT_KEYS).default("capability"),
  window: hotspotWindowSchema.default(DEFAULT_HOTSPOT_WINDOW),
  filters: hotspotFiltersSchema.default({}),
  /** Let secondary capability tags add pressure too (off by default). */
  includeSecondary: z.boolean().default(false),
});
export type HotspotListInput = z.infer<typeof hotspotListInputSchema>;

export const hotspotGetInputSchema = hotspotListInputSchema.extend({
  key: z.string().min(1).max(2100),
});
export type HotspotGetInput = z.infer<typeof hotspotGetInputSchema>;
